import { mapGetters } from 'vuex'
import { canvas } from '../settings/settings'
import * as d3 from 'd3'

export const canvasHelper = {
  data: function () {
    return {
      ...canvas,
      config: {
        canvas: {
          width: 800,
          height: 800,
          padding: 0,
          progress: 10,
          strokeWidth: 2,
          strokeColor: 'black'
        },
        blob: {
          radius: 10,
          color: 'blue',
          slideTransition: 500, // when position is changed
          hoverTransition: 200,
          hoverRadius: 15,
        },
        ghost: {
          radius: 20,
          color: 'pink'
        },
        /**
         * all values relative to the radarWidth (1 = 100)
         * size: scaling
         * x: x circle position
         * y: y circle position
         */
        radar: { size: .5, x: 0.5, y: 0.5 },
        quadrants: [
          { size: 1, x: 1, y: 1 },
          { size: 1, x: 0, y: 1 },
          { size: 1, x: 0, y: 0 },
          { size: 1, x: 1, y: 0 },
        ],
        labels: [
          { x: 0, y: 0 },
          { x: 1, y: 0 },
          { x: 1, y: 1 },
          { x: 0, y: 1 },
        ],
        /**
         * quadrant start positions
         * quadrant 0: 180-270
         * quadrant 1: 270-360
         * quadrant 2: 0-90
         * quadrant 3: 90-180
         */
        quadrantStartAngles: [
          180,
          270,
          0,
          90
        ]
      },

      isHover: false,
      quadrantBlobsCount: [0, 0, 0, 0]
    }
  },

  computed: {
    ...mapGetters({
      radar: 'radars/getCurrentRadar',
      highlightedBlobId: 'blobs/getHighlightBlobId',
      currentBlobId: 'blobs/getCurrentBlobId',
      ghostBlob: 'blobs/getGhostBlob'
    }),

    /**
     * radar quadrants
     * @returns {[*, *, *, *]}
     */
    quadrants () {
      return [this.radar.quadrant_0, this.radar.quadrant_1, this.radar.quadrant_2, this.radar.quadrant_3]
    },

    nQuadrantBlobs() {
      let nQuadrantBlobs = [0, 0, 0, 0];

      this.blobs.map(b => nQuadrantBlobs[b.quadrant]++);

      return nQuadrantBlobs;
    },

    /**
     * radar sectors
     * @returns {[*, *, *, *]}
     */
    sectors () {
      return [this.radar.sector_0, this.radar.sector_1, this.radar.sector_2, this.radar.sector_3]
    },

    /**
     * svg
     * @returns {jQuery|undefined}
     */
    svg () {
      // create svg
      return d3.select(this.$el).append('svg')
        .attr('class', 'radar')
        .attr('viewBox', [0, 0, this.config.canvas.width, this.config.canvas.height])
    },

    /**
     * calculated null (x/y center) position
     * @returns {{x: number, y: number}}
     */
    quadrantNullLocation () {
      return {
        x: this.perspective.x * this.config.canvas.width,
        y: this.perspective.y * this.config.canvas.height
      }
    },

    /**
     * sectors
     * @returns {*}
     */
    sectorScale () {
      return d3.scaleLinear()
        .domain([1, this.sectors.length])
        .range([0, this.config.canvas.width * this.perspective.size])
    },

    /**
     * width between two sectors
     * @returns {*}
     */
    sectorWidth () {
      let scale = this.sectorScale
      return scale(2)
    },

    /**
     * radar entities
     * @returns {{sectorProgressWidth: number, spreadAngle: number, sectorWidth: number, quadrantWidth: number}}
     */
    radarEntities () {
      // calculate quadrant width
      let quadrantWidth = this.config.canvas.width * this.perspective.size
      // calculate sector width
      let sectorWidth = quadrantWidth / 4

      // spread angle
      let spreadAngle = 90;

      return {
        aspectRatio: this.config.canvas.width / this.config.canvas.height,
        quadrantWidth: quadrantWidth,
        sectorWidth: sectorWidth,
        sectorProgressWidth: sectorWidth / this.config.canvas.progress, // calculate sector progress width
        spreadAngle: spreadAngle, // angle can be max 90°
        quadrantSpreadAngle: [ // for correct calculations we need to assume that every qudrant has one more blob
          spreadAngle / (this.nQuadrantBlobs[0]+1),
          spreadAngle / (this.nQuadrantBlobs[1]+1),
          spreadAngle / (this.nQuadrantBlobs[2]+1),
          spreadAngle / (this.nQuadrantBlobs[3]+1)
        ]
      }
    }
  },

  mounted () {
    this.draw()
    this.markBlob(this.currentBlobId)
  },

  methods: {
    /**
     * draw radar
     */
    draw () {
      this.drawSectors()
      this.drawQuadrant()
      this.drawBlobs()
    },

    /**
     * draw sector circles
     */
    drawSectors () {
      // remove all circles - we do not update to prevent animation
      this.svg.selectAll('circle.sector').remove()

      // get all sector circles
      var circles = this.svg.selectAll('circle.sector').data(this.sectors)

      // prepare sector scale
      var scale = this.sectorScale

      var me = this

      // create sectors
      circles.enter().append('circle')
        .attr('class', 'sector')
        .attr('cx', this.perspective.x * this.config.canvas.width)
        .attr('cy', this.perspective.y * this.config.canvas.height)
        .attr('stroke', 'none') //this.config.canvas.strokeColor
        .attr('fill', function (d, i) {
          return me.colors.sector[i]
        })
        .attr('r', function (d, i) {
          return scale(1 + (i + 1) * .75)
        })
        .lower()

      // remove
      circles.exit().remove()
    },

    /**
     * draw quadrant lines
     */
    drawQuadrant: function () {
      // let x = 0;
      // let y = 0;

      // remove all quadrant lines - we do not update to prevent animation
      this.svg.selectAll('line.quadrant').remove()
      this.svg.selectAll('text.label').remove()

      // draw x line
      this.svg.append('line')
        .attr('class', 'quadrant')
        .attr('x1', this.perspective.x * this.config.canvas.width)
        .attr('y1', 0)
        .attr('x2', this.perspective.x * this.config.canvas.width)
        .attr('y2', this.config.canvas.height)
        .attr('stroke', this.config.canvas.strokeColor)
        .attr('stroke-width', this.config.canvas.strokeWidth)

      // draw y line
      this.svg.append('line')
        .attr('class', 'quadrant')
        .attr('x1', 0)
        .attr('y1', this.perspective.y * this.config.canvas.width)
        .attr('x2', this.config.canvas.width)
        .attr('y2', this.perspective.y * this.config.canvas.height)
        .attr('stroke', this.config.canvas.strokeColor)
        .attr('stroke-width', this.config.canvas.strokeWidth)

      var me = this
      let labels = this.svg.selectAll('text.label').data(this.quadrantLabels)
      labels.enter()
        .append('a')
          .on('click', function () {
            // @todo open quadrant on label click
          })
          .append('text')
            .attr('font-family', 'sans-serif')
            .attr('font-size', '30px')
            .attr('font-weight', 'bold')
            .style('fill', function (i) {
              return me.colors.quadrant[i]
            })
            .text(function (i) {
              return me.quadrants[i]
            })
            .attr('class', 'label')
            .attr('transform', function (i) {
              let measure = d3.select(this).node().getBBox()

              let x = me.config.labels[i].x > 0 ? me.config.labels[i].x * me.config.canvas.width - measure.width : 0
              let y = me.config.labels[i].y > 0 ? me.config.labels[i].y * me.config.canvas.height - 10 : measure.height

              return 'translate(' + [x, y] + ')'
            })
    },

    /**
     * draw all blobs
     */
    drawBlobs: function () {
      var blobs = this.svg.selectAll('g.blob').data(this.blobs)

      // reset quadrant blob count
      this.quadrantBlobsCount = [0, 0, 0, 0];

      var me = this

      // create sectors
      blobs.enter().append('g')
        .attr('id', function (blob) {
          return 'blob_' + blob.id
        })
        .attr('class', function (blob) {
          return 'blob quadrant_' + blob.quadrant
        })
        .attr('transform', function (blob) {
          return 'translate(' + me.calculateBlobPosition(blob) + ')'
        })
        .append('circle')
        .attr('r', me.config.blob.radius)
        .attr('fill', me.config.blob.color)
        .on('mouseover', function (blob) {
          // lock hover event
          me.isHover = true
          // change highlighted blob id
          me.$store.commit('blobs/setHighlightBlobId', blob.id)
        })
        .on('mouseout', function () {
          // clear highlighted blob id
          me.$store.commit('blobs/clearHighlightBlobId')

          // unlock hover event
          me.isHover = false
        })
        .on('click', function (blob) {
          me.$router.push({ name: 'blob', params: { blobId: blob.id } }).catch(() => {})
        })

      // update blobs
      blobs
        .transition()
        .duration(me.config.blob.slideTransition)
        .attr('transform', function (blob, i) {
          return 'translate(' + me.calculateBlobPosition(blob, i) + ')'
        })

      // remove
      blobs.exit().remove()
    },

    /**
     * remove all blobs from canvas
     */
    resetBlobs: function () {
      this.svg.selectAll('g.blob').remove()
    },

    /**
     * calculate blob position - kind regards from Mr. Pythagoras
     * @param blob
     * @param i
     * @returns {*[]}
     */
    calculateBlobPosition (blob, i) {
      // calculate radius
      let radius = (blob.sector * this.radarEntities.sectorWidth) + ((this.config.canvas.progress - blob.sector_progress) * this.radarEntities.sectorProgressWidth)

      if(i === undefined) {
        i = this.quadrantBlobsCount[blob.quadrant]++;
      }

      /**
       * radiant
       * 90° = Pi/2
       * 180° = Pi
       * 270° = 3*Pi/2
       * 380° = 2*Pi
       */
      // calculate angle
      // let angle = (this.config.quadrantStartAngles[blob.quadrant] + i * this.radarEntities.spreadAngle);
      let angle = (this.config.quadrantStartAngles[blob.quadrant] + ((i+1) * this.radarEntities.quadrantSpreadAngle[blob.quadrant]));
      // calculate radiants => radiant = degrees * (Pi/180)
      let radiant = angle  * (Math.PI / 180);

      // x = r * cos(phi) => plus the perspective null point position
      let bx = radius * Math.cos(radiant) + this.quadrantNullLocation.x
      // y = r * sin(phi) => plus the perspective null point position
      let by = radius * Math.sin(radiant) + this.quadrantNullLocation.y

      return [bx, by]
    },

    markBlob (id) {
      this.svg.selectAll('g.blob.active')
        .classed('active', false)
        .select('circle')
        .transition()
        .duration(this.config.blob.hoverTransition)
        .attr('r', this.config.blob.radius)

      {
        this.svg.select('g.blob#blob_' + id)
          .classed('active', true)
          .select('circle')
          .transition()
          .duration(this.config.blob.hoverTransition)
          .attr('r', this.config.blob.hoverRadius)
      }
    }

  },
  watch: {
    /**
     * remove all blobs
     * => ensure that blobs are reinitialised to avoid conflicts
     */
    radar: function () {
      this.resetBlobs()
    },

    /**
     * update blob positions
     */
    blobs: function () {
        this.drawBlobs()
    },

    highlightedBlobId: function (id) {

      if ((id === 0) || (id != this.currentBlobId)) {
        // only react if change is not triggered from svg blob
        if (id === 0) {
          this.svg.selectAll('g.blob.highlighted')
            .classed('highlighted', false)
            .filter(function () {
              return !this.classList.contains('active')
            })
            .select('circle')
            .transition()
            .duration(this.config.blob.hoverTransition)
            .attr('r', this.config.blob.radius)
        } else {
          this.svg.select('g.blob#blob_' + id)
            .classed('highlighted', true)
            .select('circle')
            .transition()
            .duration(this.config.blob.hoverTransition)
            .attr('r', this.config.blob.hoverRadius)
        }
      }
    },

    ghostBlob: function (ghostBlob) {

      if (ghostBlob !== null) {
        var ghost = this.svg.selectAll('g#ghost').data([ghostBlob])

        let me = this
        ghost.enter().append('g')
          .attr('id', 'ghost')
          .attr('transform', function (ghost) {
            return 'translate(' + me.calculateBlobPosition(ghost, ghost.pos) + ')'
          })
          .append('circle')
          .attr('r', me.config.ghost.radius)
          .attr('fill', me.config.ghost.color)
      } else {
        this.svg.selectAll('g#ghost').remove()
      }

    },

    currentBlobId: function (id) {
      this.markBlob(id)
    }
  }
}
